/**
 * Errors of `/human-tasks/*` (spec §6). They arrive from the src/axios.js
 * interceptor as `{ message: <response body>, internalCode, httpCode }`, the
 * body being `{ error: { code, message } }` like the conversation limit; the
 * example-data service throws errors with `code` directly.
 */
export const getHumanTaskErrorCode = (error) => {
  if (!error || typeof error !== 'object') return null;
  // The body wins: `error.code` can also be axios' own (`ECONNABORTED`).
  const body = error.message;
  if (body && typeof body === 'object') {
    if (body.error && typeof body.error.code === 'string') return body.error.code;
    if (typeof body.code === 'string') return body.code;
  }
  return typeof error.code === 'string' ? error.code : null;
};

const ERROR_MESSAGES = {
  FEATURE_DISABLED: 'Las tareas no están activas en tu empresa.',
  TASK_NOT_FOUND: 'Esta tarea ya no existe.',
  TASK_NOT_OWNED: 'Esta tarea ya no es tuya.',
  TASK_NOT_VISIBLE: 'Esta tarea ya no está disponible.',
  HUMAN_ONLY_CONFIRM_REQUIRED: 'Confirma que quieres pasar a otro compañero una tarea que el agente IA no puede hacer.',
  INVALID_OUTCOME: 'Elige un resultado de la lista.',
  INVALID_REQUEST: 'Revisa los datos e intenta de nuevo.',
  FORBIDDEN: 'No tienes permiso para hacer esto.',
  HUMAN_ONLY: 'El agente IA no puede hacer esta tarea. Ciérrala con un resultado cuando la termines.',
  ASSIGN_FAILED: 'No se pudo asignarte la conversación. Intenta de nuevo en unos segundos.',
};

/**
 * Refusals of the panel built for one task at a time (D28 retired them). The
 * backend only sends them while its old unique index still exists, which the
 * rollout removes before this panel ships: if one arrives, it is a rollout
 * slip worth reporting, and it has no copy that tells the agent to work on
 * one task at a time.
 */
const LEGACY_LIMIT_CODES = ['ACTIVE_TASK_EXISTS', 'PAUSED_LIMIT'];

const FALLBACK_MESSAGE = 'Ocurrió un error. Intenta de nuevo.';

export const humanTaskErrorMessage = (error) => ERROR_MESSAGES[getHumanTaskErrorCode(error)] || FALLBACK_MESSAGE;

const isServerError = (error) => {
  const httpCode = error && error.httpCode;
  return typeof httpCode === 'number' && httpCode >= 500;
};

/**
 * An action that failed server-side, without a contract code, or with a
 * retired one-task code: a bug worth reporting, not an expected refusal.
 */
export const isUnexpectedHumanTaskError = (error) => {
  const code = getHumanTaskErrorCode(error);
  return isServerError(error) || !code || LEGACY_LIMIT_CODES.includes(code);
};

/**
 * Polled reads (`/me`, the stats) only report 5xx: a 404 while the backend is
 * not deployed, or a dropped connection, would otherwise report every minute.
 */
export const isReportableReadError = (error) => isServerError(error);

/** Builds a contract error, as the backend would send it (used by the example data). */
export const humanTaskError = (code) => {
  const error = new Error(code);
  error.code = code;
  return error;
};
